import { Modal } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import React, { memo, useCallback, useRef, useState } from "react";
import { COLORS } from "../../../core/consts/redesign/commonRedesign";
import { CheckBox, CheckedBox, Close } from "../../../core/consts/redesign/icons";
import partnerService from '../../../core/services/PartnerService';
import { BodyParamsPostFile, LeadDocExampleType, UploadingLeadDocExampleType } from '../../../core/types/partnerApiTypes';
import ButtonCustom from "../../../UIcomponentsRedesign/ButtonCustom/ButtonCustom";
import IconButtonMain from "../../../UIcomponentsRedesign/IconButtonMain/IconButtonMain";
import SpinnerMain from '../../../UIcomponentsRedesign/SpinnerMain';
import SwitcherCustom from '../../../UIcomponentsRedesign/SwitcherCustom';
import { TextFieldWithBorder } from '../../../UIcomponentsRedesign/TextFieldWithBorder';
import DocPreview from '../doc-preview/DocPreview';
import DocPreviewUploading from '../doc-preview/DocPreviewUploading';
import { useStyles } from './AskDocModalStyles';

const AcceptedFormats: { [index: string]: string } = {
  "DOC,DOCX": "DOC, DOCX",
  "XLS,XLSX": "XLS, XLSX",
  "PDF,TIFF": "PDF, TIFF",
  "TXT": "TXT",
};

const initialFormats = {
  "DOC,DOCX": false,
  "XLS,XLSX": false,
  "PDF,TIFF": false,
  "TXT": false,
};

type PropsType = {
  leadId: number;
  handleConfirm?: () => void;
  open: boolean;
  handleClose: () => void;
  disableCancelButton?: boolean;
  setRenew: React.Dispatch<React.SetStateAction<boolean>>;
  handleError: (error: boolean) => void;
};

const AskDocModal = ({
  leadId,
  handleClose,
  handleConfirm,
  open,
  setRenew,
  handleError,
}: PropsType) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [docName, setDocName] = useState<string>("");
  const [docComment, setDocComment] = useState<string>("");
  const [requiredDoc, setRequiredDoc] = useState<boolean>(false);
  const [edsNeed, setEdsNeed] = useState<boolean>(false);
  const [isDocPack, setIsDocPack] = useState<boolean>(false);
  const [documentIds, setDocumentIds] = useState<number[]>([]);
  const [addTemplate, setAddTemplate] = useState<boolean>(false);
  const [uploadedTemplates, setUploadedTemplates] = useState<UploadingLeadDocExampleType[]>([]);
  const [formats, setFormats] = useState<{ [index: string]: boolean }>(initialFormats);
  const disableConfirmButton = !docName || (addTemplate && uploadedTemplates.length === 0);
  const docInputRef = useRef<HTMLInputElement>(null);
  const uploadMoreFile = uploadedTemplates.length > 0;

  const closeModal = () => {
    handleClose && handleClose();
    setUploadedTemplates([])
  };

  const confirmAction = async () => {
    try {
      await handlerAskDocs();
      if (handleConfirm) {
        handleConfirm();
      }
    } catch (error) {
      console.error("Error confirming action:", error);
    }
  };

  const handlerAskDocs = () => {
    setIsLoading(true);
    const maxFileValue = isDocPack ? 0 : 1;
    const B: BodyParamsPostFile = {
      leadId: leadId,
      fileName: docName,
      isRequired: requiredDoc,
      maxFile: maxFileValue,
      isSignatureRequired: edsNeed,
    };

    if (docComment) {
      B.comment = docComment;
    }

    if (formats && selectedFormats) {
      B.format = selectedFormats;
    }
    if (documentIds && documentIds.length > 0) {
      B.documentsIds = documentIds;
    }

    partnerService
      .postAskDocService(B)
      .then((res: any) => {
        setRenew(true);
        setIsLoading(false);
        handleError(false);
        handleClose();
      })
      .catch((error) => {
        setIsLoading(false);
        console.log(error);
        handleError(true);
        handleClose();
      });
  };

  const handlerChangeDocName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value
    setDocName(text);
  };

  const handlerChangeDocComment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value
    setDocComment(text);
  };

  const handleRequiredChange = (checked: boolean) => {
    setRequiredDoc(checked);
  };

  const handleEdsChange = (checked: boolean) => {
    setEdsNeed(checked);
  };

  const handleAddTemplateChange = (checked: boolean) => {
    setAddTemplate(checked);
  };

  const handlePackChange = (checked: boolean) => {
    setIsDocPack(checked);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;

    if (file) {
      const newFile: UploadingLeadDocExampleType = {
        id: Date.now(),
        name: file.name,
        size: +(file.size / 1024 / 1024).toFixed(2),
        file,
        progress: 0,
        loadedMB: '0',
        totalMB: (file.size / 1024 / 1024).toFixed(2) + ' мб',
        error: undefined,
      };

      setUploadedTemplates((prevTemplates) => [...prevTemplates, newFile]);
      uploadFile(newFile);
    }
  };

  const uploadFile = (fileObj: UploadingLeadDocExampleType) => {
    const { id, file } = fileObj;
    const abortController = new AbortController();
    const formData = new FormData();
    formData.append('file', file);

    const updateProgress = (progress: number, loadedMB: string, totalMB: string) => {
      setUploadedTemplates((prevTemplates) =>
        prevTemplates.map((t) => (t.id === id ? { ...t, progress, loadedMB, totalMB } : t))
      );
    };

    partnerService.postDocTemplateService(
      leadId,
      formData,
      (percentage, loadedMB, totalMB) => updateProgress(percentage, loadedMB, totalMB),
      abortController.signal
    )
      .then((response) => {
        const serverFileId = response;
        setDocumentIds((prevIds) => [...prevIds, serverFileId]);
        setUploadedTemplates((prevTemplates) =>
          prevTemplates.map((t) => (t.id === id ? { ...t, id: serverFileId } : t))
        );
      })
      .catch((error) => {
        console.error('Upload error:', error);
        setUploadedTemplates((prevTemplates) =>
          prevTemplates.map((t) => (t.id === id ? { ...t, error: error.message } : t))
        );
      });

    abortController.signal.addEventListener('abort', () => {
      console.error('Upload aborted');
      setUploadedTemplates((prevTemplates) =>
        prevTemplates.filter((t) => t.id !== id)
      );
    });

    setUploadedTemplates((prevTemplates) =>
      prevTemplates.map((t) => (t.id === id ? { ...t, abortController } : t))
    );
  };

  const handleCancelUploading = (id: number) => {
    const file = uploadedTemplates.find((t) => t.id === id);
    if (file && file.abortController) {
      file.abortController.abort();
    }
  };

  const deleteDocHandler = useCallback(
    (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>, fileId: number) => {
      e.stopPropagation();
      setIsLoading(true)
      partnerService
        .deleteDocExampleService(fileId)
        .then((res) => {
          setUploadedTemplates((prevTemplates) =>
            prevTemplates.filter((template) => template.id !== fileId)
          );
          setIsLoading(false)
        })
        .catch((error: any) => {
          console.error(error);
        })
    }, []);

  const handleFormatsChange = (e: { target: { checked: boolean; }; }, key: string) => {
    setFormats({
      ...formats,
      [key]: e.target.checked,
    });
  };

  const selectedFormats = Object.keys(formats)
    .filter(key => formats[key])
    .join(',');

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <div className={classes.paper}>
        <div className={classes.box}>
          <h3 className={classes.title}>
            {"Запрос документа"}
          </h3>
          <IconButtonMain
            color='noBgGray'
            className={classes.closeButton}
            aria-label="close"
            icon={<Close color={COLORS.gray2} />}
            handleClick={closeModal} />
        </div>
        <span className={classes.text}>
          {"Здесь вы можете сообщить клиенту о том, какие действия и с какими документами необходимо совершить."}
        </span>
        <TextFieldWithBorder
          inputMode="text"
          tickicon="true"
          id="doc-name"
          name="doc-name"
          value={docName}
          label={"Название документа"}
          onChange={handlerChangeDocName}
        />
        <TextFieldWithBorder
          inputMode="text"
          tickicon="true"
          id="doc-comment"
          name="doc-comment"
          value={docComment}
          label={"Комментарий"}
          onChange={handlerChangeDocComment}
        />

        <div className={classes.checkBoxesBox}>
          <span className={classes.text}>{"Доступные к загрузке форматы документов"}</span>
          <div className={classes.checkRowBox}>
            {Object.keys(AcceptedFormats).map((key) => (
              <div className={classes.checkboxes} key={key}>
                <Checkbox
                  color="primary"
                  className={classes.checkbox}
                  checked={formats[key]}
                  sx={{
                    padding: '0',
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                  checkedIcon={<CheckedBox />}
                  icon={<CheckBox />}
                  onChange={(e) => handleFormatsChange(e, key)}
                />
                <div className={classes.checkboxText}>
                  {AcceptedFormats[key]}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={classes.divider} />
        <div className={classes.swithersBox}>
          <SwitcherCustom
            text={"Обязательный документ"}
            onChangeValue={handleRequiredChange}
            value={requiredDoc} />
          <SwitcherCustom
            text={"Требуется ЭЦП"}
            onChangeValue={handleEdsChange}
            value={edsNeed} />
          <SwitcherCustom
            text={"Прикрепить шаблон"}
            onChangeValue={handleAddTemplateChange}
            value={addTemplate} />
          <SwitcherCustom
            text={"Пакет документов"}
            onChangeValue={handlePackChange}
            value={isDocPack}
            id='pack-required'
            textInfo='Пакет документов'
            info='Если включено - запрашиваемый документ может загружаться множеством файлов.'
          />
        </div>
        {addTemplate ?
          <>
            <div className={classes.divider} />
            <div className={classes.templatesBox}>
              <h4 className={classes.templateTitle}>{"Прикрепленные шаблоны"}</h4>
              <p className={classes.templateSubtitle}>{"Максимальный размер: 20 мб"}</p>
            </div>
            {uploadedTemplates.length > 0 &&
              uploadedTemplates.map((file) => {
                const fileId = file.id;
                const leadDocFile: LeadDocExampleType = { id: file.id, name: file.name, size: file.size! };
                return file.progress === 100 ? (
                  <DocPreview
                    file={leadDocFile}
                    key={fileId}
                    isPartnerExample
                    handleDelete={(e, id) => deleteDocHandler(e, id)}
                  />
                ) : (
                  <DocPreviewUploading
                    file={file}
                    key={fileId}
                    handleCancel={() => handleCancelUploading(fileId)}
                  />
                );
              })}

            <input
              ref={docInputRef}
              type="file"
              onChange={(event) => handleFileSelect(event)}
              id="upload"
              hidden
            />
            <ButtonCustom
              color={uploadMoreFile ? "ghost" : "primary"}
              handleClick={() => docInputRef.current && docInputRef.current.click()}
              fullWidth>
              {uploadMoreFile ? 'Загрузить ещё документ' : 'Загрузить документ'}
            </ButtonCustom>
          </>
          : null}
        <ButtonCustom
          color="primary"
          handleClick={confirmAction}
          disabled={disableConfirmButton}
          fullWidth>
          {"Отправить"}
        </ButtonCustom>
        {isLoading &&
          <SpinnerMain fixedPosition />
        }
      </div>
    </Modal >
  );
};

export default memo(AskDocModal);
